import { Injectable, ApplicationRef } from '@angular/core';
import { SwUpdate, VersionReadyEvent, VersionEvent } from '@angular/service-worker';
import { interval, concat, Subject } from 'rxjs';
import { first, filter, takeUntil } from 'rxjs/operators';
import { environment } from 'environments/environment';

/**
 * Servicio personalizado para manejar actualizaciones del Service Worker
 *
 * Este servicio implementa:
 * - Verificación periódica de actualizaciones basada en configuración de ambiente
 * - Notificaciones cuando hay actualizaciones disponibles
 * - Activación de actualizaciones y recarga de la aplicación
 * - Recuperación automática ante estados irrecuperables del SW
 * - Manejo de fallos en la instalación de nuevas versiones
 *
 * Intervalos de verificación:
 * - Desarrollo: Deshabilitado (SW no se registra)
 * - QA: Cada 5 minutos
 * - Producción: Cada 1 hora
 */
@Injectable({
  providedIn: 'root'
})
export class SwUpdateService {

  private destroy$ = new Subject<void>();

  /**
   * Número máximo de reintentos para la verificación de actualizaciones
   * @private
   */
  private readonly MAX_CHECK_RETRIES = 3;

  /**
   * Tiempo base de espera entre reintentos (en ms). Se multiplica exponencialmente.
   * @private
   */
  private readonly RETRY_BASE_DELAY_MS = 2000;

  // Observable para suscribirse a eventos de actualización disponible
  public updateAvailable$ = new Subject<VersionReadyEvent>();

  constructor(
    private swUpdate: SwUpdate,
    private appRef: ApplicationRef
  ) { }

  /**
   * Inicializa el servicio de actualizaciones del Service Worker
   * Solo se ejecuta si el Service Worker está habilitado y soportado
   */
  public initialize(): void {
    // Verificar si el Service Worker está habilitado en el ambiente
    if (!environment.serviceWorker.enabled) {
      console.warn('🎈 [srvceWrkr] Service Worker deshabilitado en este ambiente');
      return;
    }

    // Verificar si el navegador soporta Service Workers
    if (!this.swUpdate.isEnabled) {
      console.warn('[srvceWrkr] Service Worker no está disponible en este navegador');
      return;
    }
    console.info(` 🚀 [srvceWrkr] `);
    // Escuchar eventos de nueva versión disponible
    this.listenForUpdates();

    // Escuchar eventos de fallo en la instalación de nuevas versiones
    this.listenForInstallationFailures();

    // Configurar verificación periódica de actualizaciones
    this.scheduleUpdateChecks();

    // Escuchar eventos de actualizaciones no recuperables
    this.listenForUnrecoverableState();
  }

  /**
   * Escucha eventos cuando una nueva versión está lista para ser activada
   */
  private listenForUpdates(): void {
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        takeUntil(this.destroy$)
      )
      .subscribe(event => {

        console.log('🚀 [Service Worker] ¡Nueva versión disponible y lista para usar!');
        console.log('📦 Versión nueva:', event.latestVersion);
        console.log('🕰️ Versión actual:', event.currentVersion);

        // Emitir evento para que componentes puedan reaccionar
        this.updateAvailable$.next(event);

        // activar automáticamente la actualización
        this.activateUpdate();


      });
  }

  /**
   * @description Escucha eventos de fallo en la instalación de una nueva versión del Service Worker.
   * Cuando la descarga de una nueva versión falla (por ejemplo, archivos con hash que ya
   * no existen en el servidor tras un nuevo deploy), se ejecuta una recuperación forzada
   * para desregistrar el SW corrupto y recargar la aplicación.
   *
   * @private
   * @returns {void}
   */
  private listenForInstallationFailures(): void {
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt: VersionEvent) => evt.type === 'VERSION_INSTALLATION_FAILED'),
        takeUntil(this.destroy$)
      )
      .subscribe((event: VersionEvent) => {
        console.error('❌ [srvceWrkr] Falló la instalación de nueva versión:', event);
        // Los archivos del nuevo manifiesto ya no están disponibles en el servidor.
        // Forzar recuperación: desregistrar SW corrupto, limpiar caché y recargar.
        this.forceRecovery('VERSION_INSTALLATION_FAILED');
      });
  }

  /**
   * Programa verificaciones periódicas de actualizaciones
   * Basado en el intervalo configurado en environment
   */
  private scheduleUpdateChecks(): void {
    const updateCheckInterval = environment.serviceWorker.updateCheckInterval;

    if (updateCheckInterval <= 0) {
      console.warn('[srvceWrkr] Verificación periódica deshabilitada');
      return;
    }

    // Esperar a que la aplicación esté estable antes de comenzar
    const appIsStable$ = this.appRef.isStable.pipe(
      first(isStable => isStable === true)
    );

    // Crear observable que emite cada X tiempo según configuración
    const everyInterval$ = interval(updateCheckInterval);

    // Concatenar: esperar estabilidad, luego verificar periódicamente
    concat(appIsStable$, everyInterval$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        console.warn('[srvceWrkr] Verificando actualizaciones...');
        this.checkForUpdates();
      });
  }

  /**
   * @description Verifica manualmente si hay actualizaciones disponibles.
   * Implementa un mecanismo de retry con backoff exponencial para manejar
   * fallos transitorios de red o del servidor.
   *
   * @param {number} [retryCount=0] - Número del intento actual (uso interno para recursión).
   * @returns {void}
   */
  public checkForUpdates(retryCount: number = 0): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.checkForUpdate()
      .then(updateFound => {
        if (updateFound) {
          console.warn('[srvceWrkr] Actualización encontrada');
        } else {
          console.warn('[srvceWrkr] No hay actualizaciones disponibles');
        }
      })
      .catch(err => {
        console.error('[srvceWrkr] Error al verificar actualizaciones:', err);

        // Reintentar con backoff exponencial si no se ha agotado el máximo de intentos
        if (retryCount < this.MAX_CHECK_RETRIES) {
          const delay = this.RETRY_BASE_DELAY_MS * Math.pow(2, retryCount);
          console.warn(`[srvceWrkr] Reintentando verificación en ${delay / 1000}s (intento ${retryCount + 1}/${this.MAX_CHECK_RETRIES})...`);
          setTimeout(() => this.checkForUpdates(retryCount + 1), delay);
        } else {
          console.error(`[srvceWrkr] Se agotaron los reintentos (${this.MAX_CHECK_RETRIES}). Ejecutando recuperación forzada.`);
          this.forceRecovery('CHECK_UPDATE_FAILED');
        }
      });
  }

  /**
   * Activa la actualización y recarga la aplicación
   */
  public activateUpdate(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    console.warn('[srvceWrkr] Activando actualización...');

    this.swUpdate.activateUpdate()
      .then(() => {
        console.warn('✅ Actualización aplicada con éxito. Recargando para mostrar los cambios...');
        // Recargar la página para aplicar la nueva versión
        document.location.reload();
      })
      .catch(err => {
        console.error('🔴 Error al activar actualización:', err);
      });
  }


  /**
   * @description Maneja estados irrecuperables del Service Worker.
   * Esto ocurre cuando el SW entra en un estado inválido donde no puede servir
   * la aplicación correctamente (por ejemplo, archivos cacheados corruptos o
   * hash mismatch tras un despliegue).
   *
   * Ejecuta una recuperación forzada automática para restaurar la funcionalidad.
   *
   * @private
   * @returns {void}
   */
  private listenForUnrecoverableState(): void {
    this.swUpdate.unrecoverable
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        console.error('[srvceWrkr] ⛔ Estado irrecuperable del Service Worker:', event.reason);
        // Recuperación automática: desregistrar el SW corrupto y recargar
        this.forceRecovery('UNRECOVERABLE_STATE');
      });
  }

  /**
   * @description Fuerza la recuperación del Service Worker como último recurso.
   * Este método desregistra todos los Service Workers activos, limpia todos los caches
   * del navegador y recarga la aplicación para restaurar un estado funcional limpio.
   *
   * Se utiliza cuando el SW entra en estado irrecuperable, cuando falla la instalación
   * de una nueva versión, o cuando se agotan los reintentos de verificación de actualizaciones.
   *
   * @param {string} reason - Razón que originó la recuperación forzada (para logging).
   * @returns {void}
   */
  private forceRecovery(reason: string): void {
    console.warn(`🔧 [srvceWrkr] Iniciando recuperación forzada. Motivo: ${reason}`);

    // Verificar soporte de Service Worker en el navegador
    if (!('serviceWorker' in navigator)) {
      console.warn('[srvceWrkr] navigator.serviceWorker no disponible, recargando directamente.');
      document.location.reload();
      return;
    }

    // Desregistrar todos los Service Workers y limpiar caches
    navigator.serviceWorker.getRegistrations()
      .then(registrations => {
        const unregisterPromises = registrations.map(reg => {
          console.warn(`[srvceWrkr] Desregistrando SW con scope: ${reg.scope}`);
          return reg.unregister();
        });
        return Promise.all(unregisterPromises);
      })
      .then(() => {
        // Limpiar todos los caches del navegador
        if ('caches' in window) {
          return caches.keys().then(cacheNames => {
            const deletePromises = cacheNames.map(cacheName => {
              console.warn(`[srvceWrkr] Eliminando cache: ${cacheName}`);
              return caches.delete(cacheName);
            });
            return Promise.all(deletePromises);
          });
        }
        return Promise.resolve([]);
      })
      .then(() => {
        console.warn('✅ [srvceWrkr] Recuperación completada. Recargando aplicación...');
        // Recargar sin caché del navegador
        document.location.reload();
      })
      .catch(err => {
        console.error('🔴 [srvceWrkr] Error durante recuperación forzada:', err);
        // Último recurso: recargar de todas formas
        document.location.reload();
      });
  }

  /**
   * Limpia las suscripciones cuando el servicio se destruye
   */
  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
